import knownProperties from 'known-css-properties/data/all.json';

const cssPropertiesSet = new Set(knownProperties.properties.filter((property) => !property.startsWith('-')));
const sortedCssProperties = [...cssPropertiesSet].sort((a, b) => b.length - a.length);

/**
 * @typedef {import('./types').ParsedVariable} ParsedVariable
 * @typedef {import('./types').ParsedVariableTree} ParsedVariableTree
 */

/**
 * Parses a flat record of CSS variables into a nested tree structure based on their names.
 * For example, '--color-primary-text: #000' becomes { color: { primary: { text: { ... } } } }.
 * @param {Record<string, string>} variables - A flat key-value object of CSS variables.
 * @returns {ParsedVariableTree} A nested object representing the hierarchical structure of the CSS variables.
 */
export const parseCssVariables = (variables) => {
  /** @type {ParsedVariableTree} */
  const result = {};

  /**
   * Adds a variable to the result tree at a specified path.
   * @param {string[]} path - The array of keys representing the path in the tree.
   * @param {string} property - The detected CSS property.
   * @param {string} variable - The full original CSS variable name.
   * @param {string} value - The variable's value.
   * @returns {void}
   */
  const addToResult = (path, property, variable, value) => {
    // Simple guard against empty paths
    if (path.length === 0) {
      console.warn(`Attempted to add variable "${variable}" with empty path.`);
      return;
    }

    /** @type {ParsedVariableTree} */
    let current = result;
    for (let i = 0; i < path.length - 1; i++) {
      const part = path[i];
      if (!current[part]) {
        current[part] = {};
      } else if (typeof current[part] !== 'object' || current[part] === null || Array.isArray(current[part])) {
        // Handle collision: Trying to create a nested object where a leaf node already exists
        console.warn(`Path collision for variable "${variable}" at segment "${part}". Overwriting existing value.`);
        current[part] = {};
      }
      current = /** @type {ParsedVariableTree} */ (current[part]);
    }

    const leafKey = path[path.length - 1];

    // Optional: Handle collision where a nested object exists at the leaf position
    if (
      typeof current[leafKey] === 'object' &&
      current[leafKey] !== null &&
      !Object.prototype.hasOwnProperty.call(current[leafKey], 'variable')
    ) {
      console.warn(
        `Path collision for variable "${variable}". Cannot place leaf node "${leafKey}" where a nested object exists.`
      );
      // Skip adding this leaf to avoid destroying structure
      return;
    }

    current[leafKey] = {
      label: property.length ? property : path.at(-1),
      variable,
      property: property.length ? property : undefined,
      value
    };
  };

  Object.entries(variables).forEach(([variable, value]) => {
    let name = variable.replace('--', '');
    if (!name) {
      console.warn(`Skipping variable "${variable}" - empty name after removing '--'.`);
      return; // Skip empty names
    }

    let property = '';
    /** @type {string[]} */
    let path;
    const originalNameForSplitting = name; // Keep original if needed for splitting later

    if (cssPropertiesSet.has(name)) {
      // 1. Check for Exact Match first
      property = name;
      path = [property];
    } else {
      // 2. If not an exact match, check for Suffix Match
      let foundSuffix = false;
      // Use sorted list (longest first) for more specific suffix matches
      for (const prop of sortedCssProperties) {
        if (name.endsWith(`-${prop}`)) {
          property = prop;
          // Shorten name ONLY when a suffix match is found
          name = name.slice(0, name.length - prop.length - 1);
          foundSuffix = true;
          break;
        }
      }

      // 3. Determine Path for Suffix Match or No Match cases
      // Split the potentially shortened name (if suffix was found)
      // or the original name (if no suffix was found)
      path = name.split('-').filter((part) => part !== '');

      if (foundSuffix) {
        // Suffix was found: Append the property name to the path
        // Handle cases where splitting the remaining name results in an empty array (e.g., --theme-color -> name='', path=)
        if (path.length === 0) {
          path = [property];
        } else {
          path.push(property);
        }
      } else {
        // No exact or suffix match found.
        // Path is just the split parts of the original name.
        // If splitting the original name yields nothing, we might need to reconsider or log.
        if (path.length === 0) {
          // Fallback check using original name splitting, just in case `name` became empty somehow
          path = originalNameForSplitting.split('-').filter((part) => part !== '');
          if (path.length === 0) {
            console.warn(`Skipping variable "${variable}" - resulted in empty path after splitting.`);
            return; // Skip if splitting yields nothing meaningful
          }
        }
      }
    }

    // Final check and add to result
    if (path.length > 0) {
      addToResult(path, property, variable, value);
    } else {
      console.warn(`Skipping variable "${variable}" - final path is empty.`);
    }
  });

  return result;
};

/**
 * Type guard to check if a value is a leaf node (a ParsedVariable) in the tree.
 * @param {*} value - The value to check.
 * @returns {value is ParsedVariable} True if the value is a ParsedVariable object.
 */
export const isLeafNode = (value) => {
  return (
    typeof value === 'object' && value !== null && !Array.isArray(value) && 'label' in value && 'variable' in value
  );
};

/**
 * Type guard to check if a value is a nested object (a branch) in the tree.
 * @param {*} value - The value to check.
 * @returns {value is ParsedVariableTree} True if the value is a branch node in the tree.
 * @see isLeafNode
 */
export const isNestedObject = (value) => {
  return typeof value === 'object' && value !== null && !Array.isArray(value) && !isLeafNode(value);
};

/**
 * Checks if a value is any kind of object node in the tree (branch or leaf).
 * @param {*} value - The value to check.
 * @returns {value is ParsedVariable | ParsedVariableTree} True if the value is any non-null, non-array object.
 */
export const isObject = (value) => {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
};
